/**
 * Login.js - Sign In Component
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 * Updated: Changed to sign-up-first flow - this is now sign-in only
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import LoadingSpinner from './LoadingSpinner';
import './Login.css';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { resolveReturnPath, destinationAfterAuth } from '../utils/safeReturnPath';
import SEO from './SEO';
import { useLanguage } from '../context/LanguageContext';
import { localizeAuthError } from '../i18n/serverErrors';

const Login = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    identifier: '', // Can be username or email
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnUrl = resolveReturnPath(searchParams, location.state, '/');
  // Never send new sign-ups to /admin via the Sign Up link
  const registerHref =
    returnUrl !== '/' && !returnUrl.startsWith('/admin')
      ? `/register?returnUrl=${encodeURIComponent(returnUrl)}`
      : '/register';

  const { login, currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      navigate(destinationAfterAuth(returnUrl, currentUser, '/'));
    }
  }, [currentUser, navigate, returnUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    console.log('🔐 Login: Submitting form with identifier:', formData.identifier);

    try {
      console.log('Attempting login with:', formData.identifier);
      const result = await login(formData.identifier, formData.password);
      console.log('Login result:', result);

      if (result.success) {
        showSuccess(t.auth.welcomeBack);
        const dest = destinationAfterAuth(returnUrl, result.user || null, '/');
        setTimeout(() => navigate(dest), 500);
      } else {
        const message = localizeAuthError(result.error, t.auth, 'invalidCredentials');
        setError(message);
        showError(message);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t.auth.loginFailed);
      showError(t.auth.loginFailed);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <center>
      <SEO
        title="Sign In | Cranford Publications"
        description="Sign in to your Cranford Publications account."
        canonical="/login"
        robots="noindex, follow"
      />
      {isLoading && (
        <div className="loading-spinner-fullscreen">
          <LoadingSpinner message={t.auth.loggingIn} />
        </div>
      )}
      <div className="login-box">
        <h2>{t.auth.loginTitle}</h2>
        <div className="login-links">
          <p>
            {t.auth.noAccount}{' '}
            <Link to={registerHref}>{getIcon('register')} {t.auth.signUpHere}</Link>
          </p>
          <p>
            <Link to="/forgot-password">{getIcon('mail')} {t.auth.forgotPassword}</Link>
          </p>
        </div>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-group">
            <label htmlFor="identifier">{getIcon('user')} {t.auth.usernameOrEmail}</label>
            <input
              type="text"
              id="identifier"
              name="identifier"
              value={formData.identifier}
              onChange={handleInputChange}
              placeholder={t.auth.usernameOrEmailPlaceholder}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{getIcon('login')} {t.auth.password}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t.auth.passwordPlaceholder}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                title={showPassword ? t.auth.hidePassword : t.auth.showPassword}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="login-button"
          >
            {isLoading ? t.auth.loggingIn : `${getIcon('login')} ${t.auth.loginButton}`}
          </button>
        </form>
      </div>
    </center>
  );
};

export default Login; 